/**
 * Artisan suit pips — the card's smallest visual unit.
 *
 * Public API:
 *   - `addArtisanPip(parent, suit, cx, cy, scale, color, isFlipped)` — thin dispatcher
 *     that sets up the transformed group, wires the transform-aware gradient
 *     (via `adjustGradient`), routes to the per-suit drawer, and attaches.
 *   - `addPipArrangement(parent, suit, rank, color)` — lays out 7/8/9/10 pip grids
 *     using the coordinates in `PIP_LAYOUTS`.
 *
 * Per-suit drawers (`addDiamondPip` … `addGrandPip`) are module-private: the
 * dispatcher is the only supported entry point so callers stay suit-agnostic.
 */

import { s } from "./card-svg.js";
import { adjustGradient } from "./card-gradients.js";
import { CARD_GEOMETRY } from "./card-geometry.js";

// --- Per-suit pip geometry (appended to a pre-transformed <g>) ---

/** Schellen: "The Glass Bell". Sphere with etched slit. */
function addDiamondPip(g) {
  g.appendChild(s("circle", { cx: 0, cy: 0, r: 10, opacity: 0.9 }));
  g.appendChild(
    s("path", {
      d: "M -6 3 Q 0 7.5 6 3",
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 1.25,
      "stroke-linecap": "round",
      opacity: 0.5,
    }),
  );
  g.appendChild(
    s("circle", { cx: 3.5, cy: -3.5, r: 2.5, fill: "#E2E8F0", opacity: 0.25 }),
  );
  g.appendChild(
    s("rect", { x: -1.5, y: -14, width: 3, height: 2.5, rx: 0.75 }),
  );
}

/** Herz: "The Deep Cleft". Traditional silhouette with deep top indentation and radiant core. */
function addHeartPip(g) {
  g.appendChild(
    s("path", {
      d: "M 0 14 C -3 10 -12 5 -12 -3 C -12 -9 -7 -12 -3 -12 C -1 -12 0 -9 0 -2 C 0 -9 1 -12 3 -12 C 7 -12 12 -9 12 -3 C 12 5 3 10 0 14 Z",
      opacity: 0.9,
    }),
  );
  g.appendChild(
    s("circle", { cx: 0, cy: 2, r: 4, fill: "#E2E8F0", opacity: 0.12 }),
  );
  g.appendChild(
    s("circle", { cx: 0, cy: 2, r: 2, fill: "#E2E8F0", opacity: 0.2 }),
  );
}

/** Grün: "The Glow Leaf". Lobed silhouette with glowing midrib. */
function addSpadePip(g) {
  g.appendChild(
    s("path", {
      d: "M 0 -14 C 6 -10 11 -5 11 1 C 11 6 7 7.5 5 10 L 0 14 L -5 10 C -7 7.5 -11 6 -11 1 C -11 -5 -6 -10 0 -14 Z",
      opacity: 0.9,
    }),
  );
  g.appendChild(
    s("line", {
      x1: 0,
      y1: -10,
      x2: 0,
      y2: 10,
      stroke: "#E2E8F0",
      "stroke-width": 1,
      opacity: 0.4,
    }),
  );
  g.appendChild(
    s("path", {
      d: "M 0 -5 L 6 -1 M 0 1 L 5 5 M 0 -5 L -6 -1 M 0 1 L -5 5",
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 0.5,
      opacity: 0.25,
    }),
  );
}

/** Eichel: "The Crafted Seed". Diamond-quilted cap and radiant seed. */
function addClubPip(g) {
  g.appendChild(s("ellipse", { cx: 0, cy: 6, rx: 8, ry: 10, opacity: 0.9 }));
  g.appendChild(
    s("circle", { cx: 0, cy: 6, r: 3.5, fill: "#E2E8F0", opacity: 0.12 }),
  );
  g.appendChild(
    s("path", { d: "M -10 2 Q -10 -9 0 -9 Q 10 -9 10 2 L -10 2 Z" }),
  );
  g.appendChild(
    s("path", {
      d: "M -4 -4 L 0 -7 L 4 -4 L 0 -1 Z",
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 0.6,
      opacity: 0.3,
    }),
  );
  g.appendChild(s("rect", { x: -1, y: -13, width: 2, height: 4, rx: 0.75 }));
}

/** Grand: "The Wine Vessel". Elegant chalice with crystalline facets. */
function addGrandPip(g) {
  g.appendChild(
    s("path", {
      d: "M -11 -9 C -13 -22 13 -22 11 -9 C 11 0 5 5 0 6 C -5 5 -11 0 -11 -9 Z",
      opacity: 0.9,
    }),
  );
  g.appendChild(
    s("path", {
      d: "M -10 -5 Q 0 -1 10 -5",
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 1,
      opacity: 0.2,
    }),
  );
  g.appendChild(
    s("rect", { x: -1, y: 6, width: 2, height: 12, rx: 0.5, opacity: 0.7 }),
  );
  g.appendChild(s("circle", { cx: 0, cy: 12, r: 3 }));
  g.appendChild(
    s("circle", { cx: 0, cy: 12, r: 1, fill: "#E2E8F0", opacity: 0.3 }),
  );
  g.appendChild(s("ellipse", { cx: 0, cy: 22, rx: 10, ry: 3, opacity: 0.6 }));
}

// --- Dispatcher ---

/** Appends an Artisan pip to `parent` at (cx, cy), scaled and colored. */
export function addArtisanPip(parent, suit, cx, cy, scale, color, isFlipped) {
  color = adjustGradient(parent, color, cy, scale, isFlipped);
  const g = s("g", {
    transform: `translate(${cx},${cy}) scale(${scale})`,
    fill: color,
  });
  switch (
    suit // size: flat dispatch
  ) {
    case "diamonds":
      addDiamondPip(g);
      break;
    case "hearts":
      addHeartPip(g);
      break;
    case "spades":
      addSpadePip(g);
      break;
    case "clubs":
      addClubPip(g);
      break;
    case "grand":
      addGrandPip(g);
      break;
  }
  parent.appendChild(g);
}

// --- Number card pip arrangements (7-10) ---

// Pip positions within the center area (viewBox 250x350).
// x: left column ~85, center ~125, right column ~165
// y: spread from ~95 to ~255, avoiding corner label areas
const PIP_LAYOUTS = {
  seven: [
    [78, 118],
    [108, 118],
    [138, 118],
    [168, 118],
    [152, 162],
    [136, 208],
    [120, 252],
  ],
  eight: [
    [125, 118],
    [85, 148],
    [165, 148],
    [105, 185],
    [148, 185],
    [85, 222],
    [165, 222],
    [125, 255],
  ],
  nine: [
    [105, 115],
    [150, 115],
    [82, 150],
    [172, 150],
    [105, 185],
    [150, 185],
    [172, 220],
    [150, 255],
    [105, 255],
  ],
  ten: [
    [66, 120],
    [66, 168],
    [66, 216],
    [66, 258],
    [148, 118],
    [178, 158],
    [178, 218],
    [148, 258],
    [118, 218],
    [118, 158],
  ],
};

/**
 * Lays out a number card's pip grid (7-10) for the given suit. No-op for face/ace ranks.
 * Each pip's own size scales by `CARD_GEOMETRY.pipSizeScale` (direct multiplication, no wrapper,
 * same trick as `atmosphereScaleAce`; golden by default since 2026-07-19, DESIGN #15 — pips at
 * 0.75 × φ ≈ 1.214 effective scale, was 0.75 × 1 = 0.75).
 */
export function addPipArrangement(parent, suit, rank, color) {
  const positions = PIP_LAYOUTS[rank];
  if (!positions) return;
  const { pipSizeScale } = CARD_GEOMETRY;
  for (const [cx, cy] of positions) {
    addArtisanPip(parent, suit, cx, cy, 0.75 * pipSizeScale, color);
  }
}
